/**
 * Determines whether a restrictedUntil date is still active (restriction has not yet expired).
 * Returns true when the date is defined, valid, and in the future.
 */
export const getIsRestrictionActive = (restrictedUntil?: string): boolean => {
  if (!restrictedUntil) {
    return false;
  }

  const timestamp = Date.parse(restrictedUntil);

  return !Number.isNaN(timestamp) && timestamp > Date.now();
};

interface MethodAvailabilityFields {
  enabled: boolean;
  disabledByCategory: boolean;
  restrictedUntil?: string;
}

/**
 * Determines whether a payment method is currently available for selection.
 *
 * Priority order:
 * 1. `restrictedUntil` active → NOT available (timer still running)
 * 2. `enabled` is false → NOT available (base availability from limits/wager/backend)
 * 3. `restrictedUntil` expired → available (timer ended, category block is lifted)
 * 4. `disabledByCategory` is true AND no restriction → NOT available (permanently disabled)
 */
export const isMethodAvailable = ({
  enabled,
  disabledByCategory,
  restrictedUntil,
}: MethodAvailabilityFields): boolean => {
  const hasRestriction = Boolean(restrictedUntil);
  const isRestrictionActive = getIsRestrictionActive(restrictedUntil);

  // Active restriction → method is unavailable (timer still running)
  if (isRestrictionActive) {
    return false;
  }

  // Base availability check (limits, wager, backend flag) — always applies
  if (!enabled) {
    return false;
  }

  // Expired restriction → category block is lifted, method is available
  if (hasRestriction) {
    return true;
  }

  return !disabledByCategory;
};
